/* eslint-env browser */
// Add booking (approved item M9, board Portal_After): the money form agents
// use while the customer is on the line. Step 1 "Get fare" (the one primary
// button), step 2 the fare shows in the same card and "Book now" becomes the
// primary, with Cancel as a quiet text button on the leading side. Enter
// submits the form from any field. Problems show next to the form (never an
// alert box); a booking toast carries the start code with a Copy button.
// Book later uses an Addis Ababa, 12-hour date-time picker (Q17).
import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  MenuItem,
  FormControlLabel,
  FormControl,
  FormLabel,
  Radio,
  RadioGroup,
  Button,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  InputAdornment,
  useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utcPlugin from 'dayjs/plugin/utc';
import timezonePlugin from 'dayjs/plugin/timezone';
import CalculateOutlinedIcon from '@mui/icons-material/CalculateOutlined';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import DirectionsCarFilledRoundedIcon from '@mui/icons-material/DirectionsCarFilledRounded';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import GoogleMapsAutoComplete from '../components/GoogleMapsAutoComplete';
import { useSelector, useDispatch } from "react-redux";
import { api } from 'common';
import { useTranslation } from "react-i18next";
import { BookingModalBody, validateBookingObj } from 'common/sharedFunctions';
import { useLocation, useNavigate } from 'react-router-dom';
import { calcEst, showEst, optionsRequired } from '../common/sharedFunctions';
import { getLangKey } from 'common/src/other/getLangKey';
import { processApiResponse } from '../utils/securityUtils';
import { tokens as c, tones } from '../theme';
import { showToast, InlineMessage } from '../components/feedback';
import { FareTag, isFixedFare, formatMoney, calmCaps } from './BookingHistory';

// The picker reads and writes Addis Ababa time whatever the computer's own
// zone is (dayjs needs these two plugins for MUI X's `timezone` prop).
dayjs.extend(utcPlugin);
dayjs.extend(timezonePlugin);
const ADDIS_TZ = 'Africa/Addis_Ababa';
const QUARTER_MS = 15 * 60 * 1000;
// The first quarter hour at least 30 minutes from now: a sensible "later".
const defaultLaterMs = () => Math.ceil((Date.now() + 30 * 60 * 1000) / QUARTER_MS) * QUARTER_MS;
const newStartCode = () => Math.floor(Math.random() * 90000) + 10000;

const icons = {
  'paypal':require('../assets/payment-icons/paypal-logo.png').default,
  'braintree':require('../assets/payment-icons/braintree-logo.png').default,
  'stripe':require('../assets/payment-icons/stripe-logo.png').default,
  'paytm':require('../assets/payment-icons/paytm-logo.png').default,
  'payulatam':require('../assets/payment-icons/payulatam-logo.png').default,
  'flutterwave':require('../assets/payment-icons/flutterwave-logo.png').default,
  'paystack':require('../assets/payment-icons/paystack-logo.png').default,
  'securepay':require('../assets/payment-icons/securepay-logo.png').default,
  'payfast':require('../assets/payment-icons/payfast-logo.png').default,
  'liqpay':require('../assets/payment-icons/liqpay-logo.png').default,
  'culqi':require('../assets/payment-icons/culqi-logo.png').default,
  'mercadopago':require('../assets/payment-icons/mercadopago-logo.png').default,
  'squareup':require('../assets/payment-icons/squareup-logo.png').default,
  'wipay':require('../assets/payment-icons/wipay-logo.png').default,
  'test':require('../assets/payment-icons/test-logo.png').default,
  'razorpay':require('../assets/payment-icons/razorpay-logo.png').default,
  'paymongo':require('../assets/payment-icons/paymongo-logo.png').default,
  'iyzico':require('../assets/payment-icons/iyzico-logo.png').default,
  'slickpay':require('../assets/payment-icons/slickpay-logo.png').default,
  'xendit':require('../assets/payment-icons/xendit-logo.png').default,
  'chapa':require('../assets/payment-icons/chapa-logo.png').default,
  'telebirr':require('../assets/payment-icons/telebirr-logo.png').default,
  'santimpay':require('../assets/payment-icons/santimpay-logo.png').default
}

// Phone input (owner rule): plain digits, 9 of them. A pasted +251…, 251…
// or 0… and any spaces or dashes are dropped as the agent types.
const toNineDigits = (raw) => {
  let d = String(raw || '').replace(/\D/g, '');
  if (d.length > 9 && d.startsWith('251')) d = d.slice(3);
  d = d.replace(/^0+/, '');
  return d.slice(0, 9);
};

// The plain classes object OtherPerson / BookingModalBody expect; the theme
// styles their fields now.
const NO_CLASSES = {};

// The page uses the full content width. On wide screens (lg) the booking
// form is two halves in reading order: who and where on the left (phone,
// pickup, destination, car type | when, trip time), the fare and the
// booking on the right (owner, 28 Oct: a logical order). Below lg one
// column, as phones and small tablets always had, with car type and when
// side by side from sm. The fare calculator is split the same way.
const FULL_ROW = { sm: '1 / -1' };
// Booking Details' pickup and drop-off marks (green pin, red flag) on a small
// tinted disc, inside the place fields so every field keeps the same edges.
const placeMark = (Icon, tone) => (
  <Box aria-hidden="true" sx={{ width: 28, height: 28, borderRadius: '50%', bgcolor: tone.bg, color: tone.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
    <Icon sx={{ fontSize: 17 }} />
  </Box>
);

export default function AddBookings(props) {
  const { t, i18n  } = useTranslation();
  const isRTL = i18n.dir();
  const {
    getEstimate,
    clearEstimate,
    addBooking,
    clearBooking,
    MinutesPassed,
    updateBooking,
    updateProfileWithEmail,
    checkUserExists,
    getDirectionsApi,
    mainSignUp,
    sendWelcomeSms,
    fetchSingleProfile,
    editPromo,
    fetchPromos
  } = api;
  const dispatch = useDispatch();
  const theme = useTheme();
  const phoneScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const cartypes = useSelector(state => state.cartypes.cars);
  const estimatedata = useSelector(state => state.estimatedata);
  const bookingdata = useSelector(state => state.bookingdata);
  const userdata = useSelector(state => state.usersdata);
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);
  const providers = useSelector(state => state.paymentmethods.providers);
  const activeBookings = useSelector(state => state.bookinglistdata.active);
  const promos = useSelector(state => state.promodata.promos);
  const [carType, setCarType] = useState(t('select_car'));
  const [pickupAddress, setPickupAddress] = useState(null);
  const [dropAddress, setDropAddress] = useState(null);
  const [optionModalStatus, setOptionModalStatus] = useState(false);
  const [estimateModalStatus, setEstimateModalStatus] = useState(false);
  const [selectedCarDetails, setSelectedCarDetails] = useState(null);
  const [users, setUsers] = useState(null);
  // A problem with the form, shown above the buttons (replaces alert()).
  const [formError, setFormError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [laterError, setLaterError] = useState('');
  const [userCombo, setUserCombo] = useState(null);
  const [estimateRequested, setEstimateRequested] = useState(false);
  const [bookingType, setBookingType] = useState('Book Now');
  const rootRef = useRef(null);
  const phoneRef = useRef(null);
  const [tempRoute, setTempRoute] = useState();
  const [paymentModalStatus, setPaymentModalStatus] = useState(false);
  const [walletModalStatus, setWalletModalStatus] = useState(false);
  const {state} = useLocation();
  const [selectedProvider, setSelectedProvider] = useState();
  const [selectedProviderIndex, setSelectedProviderIndex] = useState(0);
  const [roundTrip, setRoundTrip] = useState(0);
  const [tripInstructions, setTripInstructions] = useState('');
  const [loading, setLoading] = useState(false);
  const [loading2, setLoading2] = useState(false);
  const [bookingOnWait,setBookingOnWait] = useState();
  const [deliveryWithBid, setDeliveryWithBid] = useState(0);
  const [otherPerson, setOtherPerson] = useState(false);
  const [offerFare, setOfferFare] = useState('');
  const [minimumPrice, setMinimumPrice] = useState(0);
  const [customerPhone, setCustomerPhone] = useState('');
  const [loadingOptions, setLoadingOptions] = useState(false);
  // The start code read to the customer: one code per booking, the same in
  // the form, on the booking and in the toast.
  const [startCode, setStartCode] = useState(newStartCode);

  // Fare Calculator States
  const [activeTab, setActiveTab] = useState(0);
  const [calcDistance, setCalcDistance] = useState('');
  const [calcTime, setCalcTime] = useState('');
  const [calcTimeUnit, setCalcTimeUnit] = useState('minutes');
  const [calculatedFare, setCalculatedFare] = useState(null);
  const [selectedCalcCarType, setSelectedCalcCarType] = useState(null);
  const [calcError, setCalcError] = useState('');

  useEffect(() => {
    if (settings && settings.bookingFlow) {
        setDeliveryWithBid(settings.bookingFlow === "2" ? 1 : 0)
    }
  }, [settings, settings.bookingFlow])

  // Promo codes are fetched on mount (no longer pre-fetched at login).
  useEffect(() => {
    dispatch(fetchPromos());
  }, [dispatch, fetchPromos]);



  const [instructionData,setInstructionData] = useState({
    otherPerson : "",
    otherPersonPhone: "",
    pickUpInstructions: "",
    deliveryInstructions: "",
    parcelTypeIndex: 0,
    optionIndex: 0,
    parcelTypeSelected: null,
    optionSelected: null
  });

  const [profileData,setProfileData] = useState({
    firstName : '',
    lastName : '',
    email: ''
  });

  const [payment_mode, setPaymentMode] = useState(2);
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [promoDiscount, setPromoDiscount] = useState(0);

  const computePromoDiscount = (promo, fare) => {
    if (!promo || !fare) return 0;
    const amount = parseFloat(fare);
    if (String(promo.promo_discount_type).toLowerCase() === 'percentage') {
      const raw = parseFloat((amount * promo.promo_discount_value) / 100);
      const capped = promo.max_promo_discount_value && raw > parseFloat(promo.max_promo_discount_value)
        ? parseFloat(promo.max_promo_discount_value)
        : raw;
      return parseFloat(capped.toFixed(settings?.decimal || 2));
    }
    return parseFloat(parseFloat(promo.max_promo_discount_value || promo.promo_discount_value).toFixed(settings?.decimal || 2));
  };

  const getActivePromos = () => {
    if (!Array.isArray(promos) || !estimatedata.estimate) return [];
    const fare = parseFloat(estimatedata.estimate.estimateFare);
    const now = Date.now();
    const riderUid = userCombo?.uid;
    return promos.filter(p => {
      if (!p || !p.promo_code) return false;
      if (p.promo_validity && now > parseInt(p.promo_validity)) return false;
      if (p.min_order && fare < parseFloat(p.min_order)) return false;
      if (p.promo_usage_limit && p.user_avail && parseInt(p.user_avail) >= parseInt(p.promo_usage_limit)) return false;
      if (riderUid && p.usersUsed && p.usersUsed[riderUid]) return false;
      return true;
    });
  };

  const handleApplyPromo = (promoId) => {
    const promo = (promos || []).find(p => p.id === promoId);
    if (!promo || !estimatedata.estimate) return;
    const fare = parseFloat(estimatedata.estimate.estimateFare);
    const now = Date.now();
    const riderUid = userCombo?.uid;
    if (promo.min_order && fare < parseFloat(promo.min_order)) {
      setFormError(t('promo_eligiblity', { defaultValue: 'This promo needs a higher fare.' }));
      return;
    }
    if (promo.promo_validity && now > parseInt(promo.promo_validity)) {
      setFormError(t('promo_exp', { defaultValue: 'This promo has expired.' }));
      return;
    }
    if (promo.promo_usage_limit && promo.user_avail && parseInt(promo.user_avail) >= parseInt(promo.promo_usage_limit)) {
      setFormError(t('promo_exp_limit', { defaultValue: 'This promo has been used up.' }));
      return;
    }
    if (riderUid && promo.usersUsed && promo.usersUsed[riderUid]) {
      setFormError(t('promo_used', { defaultValue: 'This customer has already used this promo.' }));
      return;
    }
    const discount = computePromoDiscount(promo, fare);
    setFormError('');
    setAppliedPromo(promo);
    setPromoDiscount(discount);
  };

  const handleRemovePromo = () => {
    setAppliedPromo(null);
    setPromoDiscount(0);
  };

  // Manual Fare Calculator Function
  const calculateManualFare = () => {
    if (!selectedCalcCarType) {
      setCalcError(t('please_select_car_type', { defaultValue: 'Please select a car type' }));
      return;
    }

    // Check if at least one value is provided
    if (!calcDistance && !calcTime) {
      setCalcError(t('fare_calc_enter_values', { defaultValue: 'Enter the distance, the time or both.' }));
      return;
    }

    // Parse values - allow both distance AND time
    const distance = calcDistance ? parseFloat(calcDistance) : 0;
    const timeInSeconds = calcTime ? (calcTimeUnit === 'minutes' ? parseFloat(calcTime) * 60 : parseFloat(calcTime)) : 0;

    // Validate inputs
    if ((calcDistance && (isNaN(distance) || distance < 0)) || (calcTime && (isNaN(timeInSeconds) || timeInSeconds < 0))) {
      setCalcError(t('invalid_values', { defaultValue: 'Please enter valid values' }));
      return;
    }
    setCalcError('');

    // Calculate base fare
    const baseFare = selectedCalcCarType.base_fare || 0;
    const ratePerKm = selectedCalcCarType.rate_per_unit_distance || 0;
    const ratePerHour = selectedCalcCarType.rate_per_hour || 0;
    const minFare = selectedCalcCarType.min_fare || 0;

    // Calculate distance and time costs
    const distanceCost = ratePerKm * distance;
    const timeCost = ratePerHour * (timeInSeconds / 3600); // Convert seconds to hours

    // Calculate subtotal
    let subtotal = baseFare + distanceCost + timeCost;

    // Apply minimum fare if necessary
    const tripFare = Math.max(subtotal, minFare);

    // Calculate convenience fee
    let convenienceFee = 0;
    if (selectedCalcCarType.convenience_fees && selectedCalcCarType.convenience_fees > 0) {
      if (selectedCalcCarType.convenience_fee_type === 'flat') {
        convenienceFee = selectedCalcCarType.convenience_fees;
      } else {
        convenienceFee = (tripFare * selectedCalcCarType.convenience_fees) / 100;
      }
    }

    // Calculate total
    const totalFare = tripFare + convenienceFee;

    setCalculatedFare({
      baseFare: baseFare.toFixed(settings?.decimal || 2),
      distanceCost: distanceCost.toFixed(settings?.decimal || 2),
      timeCost: timeCost.toFixed(settings?.decimal || 2),
      subtotal: subtotal.toFixed(settings?.decimal || 2),
      tripFare: tripFare.toFixed(settings?.decimal || 2),
      convenienceFee: convenienceFee.toFixed(settings?.decimal || 2),
      totalFare: totalFare.toFixed(settings?.decimal || 2),
      minFareApplied: subtotal < minFare
    });
  };

  const resetFareCalculator = () => {
    setCalcDistance('');
    setCalcTime('');
    setCalcTimeUnit('minutes');
    setCalculatedFare(null);
    setSelectedCalcCarType(null);
    setCalcError('');
  };
  const [radioProps,setRadioProps] =  useState([]);

  const navigate = useNavigate();

  useEffect(()=>{
      if(settings && providers && providers.length > 0){
        setSelectedProvider(providers[0]);
        let arr = [{ label: t('wallet'), value: 0, cat: 'wallet' }];
        let val = 0;
        if(!settings.disable_online && providers && providers.length > 0){
            val++;
            arr.push({ label: t('card'), value: val , cat: 'card' });
        }
        if(!settings.disable_cash){
            val++;
            arr.push({ label: t('cash'), value: val, cat: 'cash' });
        }
        setRadioProps(arr);
        // Cash stays the default; an out-of-range choice falls back to it.
        const cashIndex = arr.findIndex((p) => p.cat === 'cash');
        setPaymentMode((prev) => (arr[prev] ? prev : (cashIndex >= 0 ? cashIndex : 0)));
      }
  },[settings, providers, t]);

  // The payment kind picked in the form ('cash' when there is no choice).
  const payCat = (radioProps[payment_mode] && radioProps[payment_mode].cat) || 'cash';

  useEffect(() => {
    if(state && state !== null){
      let carDetails = state.carData;
      setCarType(carDetails.name)
      setInstructionData({
        deliveryPerson : "",
        deliveryPersonPhone: "",
        pickUpInstructions: "",
        deliveryInstructions: "",
        parcelTypeIndex: 0,
        optionIndex: 0,
        parcelTypeSelected: Array.isArray(carDetails.parcelTypes) ? carDetails.parcelTypes[0] : null,
        optionSelected: Array.isArray(carDetails.options) ? carDetails.options[0] : null
      })
      setSelectedCarDetails(carDetails);
    }
  }, [state]);

  const handleChange = (e) => {
    if(e.target.name === 'parcelTypeIndex'){
      setInstructionData({
        ...instructionData,
        parcelTypeIndex: parseInt(e.target.value),
        parcelTypeSelected: selectedCarDetails.parcelTypes[e.target.value]
      });
    }else if(e.target.name === 'optionIndex'){
      setInstructionData({
        ...instructionData,
        optionIndex: parseInt(e.target.value),
        optionSelected: selectedCarDetails.options[e.target.value]
      });
    }else if(e.target.name === 'payment_mode'){
      setPaymentMode(e.target.value);
    }else if(e.target.name === 'selectedProviderIndex'){
      setSelectedProviderIndex(parseInt(e.target.value));
      setSelectedProvider(providers[parseInt(e.target.value)]);
    }else if(e.target.name === 'tripInstructions'){
      setTripInstructions(e.target.value);
    }else if(e.target.name === 'roundTrip'){
      setRoundTrip(e.target.value);
    }else if(e.target.name === 'bookingFlow'){
      setDeliveryWithBid(e.target.value);
    }else if(e.target.name === 'firstName'){
      setProfileData({ ...profileData, [e.target.name]: e.target.value });
    }else if(e.target.name === 'lastName'){
      setProfileData({ ...profileData, [e.target.name]: e.target.value });
    }else if(e.target.name === 'email'){
      setProfileData({ ...profileData, [e.target.name]: e.target.value });
    }else if(e.target.name === 'offerFare'){
      setOfferFare(e.target.value);
    }else{
      setInstructionData({ ...instructionData, [e.target.name]: e.target.value });
    }
  };

  // Book later: the trip time as absolute ms, entered in Addis Ababa time.
  const [selectedDate, setSelectedDate] = useState(defaultLaterMs);

  const clearForm = () => {
    setUserCombo(null);
    setPickupAddress(null);
    setDropAddress(null);
    setSelectedCarDetails(null);
    setCarType(t('select_car'));
    setBookingType('Book Now');
    setEstimateRequested(false);
  }

  // Booking toast (M9): the reference and the start code the agent reads to
  // the customer, with Copy. It outlives the move to the bookings list.
  const copyToClipboard = (text, what) => {
    if (!text) return;
    const ok = () => showToast.info(t('copied_value', { what, defaultValue: '{{what}} copied' }));
    const fail = () => showToast.error(t('copy_failed', { defaultValue: 'Could not copy. Select the text and copy it instead.' }));
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(String(text)).then(ok, fail);
        return;
      }
    } catch (e) {
      // fall through
    }
    fail();
  };

  const announceBooking = (main) => {
    if (!main) return;
    const reference = main.reference || '';
    const code = main.otp ? String(main.otp) : '';
    const message = code
      ? t('add_booking_created_code', { reference, code, defaultValue: 'Booking {{reference}} created · start code {{code}}' })
      : t('add_booking_created', { reference, defaultValue: 'Booking {{reference}} created · finding a driver' });
    showToast.success(message, {
      actionLabel: t('add_booking_copy', { defaultValue: 'Copy' }),
      onAction: () =>
        code
          ? copyToClipboard(code, t('add_booking_start_code', { defaultValue: 'Start code' }))
          : copyToClipboard(reference, t('booking_page_reference', { defaultValue: 'Booking reference' })),
      duration: 20000,
    });
    setStartCode(newStartCode());
  };
  // The effect below runs on booking changes only; it calls the latest
  // announceBooking through this ref.
  const announceRef = useRef(announceBooking);
  announceRef.current = announceBooking;

  useEffect(() => {
    if (auth.profile && bookingOnWait) {
      setEstimateModalStatus(false);
      dispatch(addBooking(bookingOnWait));
      setBookingOnWait(null);
      setLoading(false);
    }
  }, [auth.profile,bookingOnWait,dispatch,addBooking])

  useEffect(() => {
    if (bookingdata.booking && bookingdata.booking.mainData.status === 'PAYMENT_PENDING') {
      if(bookingdata.booking.mainData.payment_mode === 'cash'){
        announceRef.current(bookingdata.booking.mainData);
        dispatch(clearBooking());
        setTempRoute(null);
        setLoading2(false);
        navigate('/bookings')
      } else{
        if(bookingdata.booking.mainData.payment_mode === 'card'){
          setLoading2(false);
          setPaymentModalStatus(true);
        }else{
          setLoading2(false);
          setWalletModalStatus(true);
        }
      }
    }
    if (bookingdata.booking && bookingdata.booking.mainData.status === 'NEW') {
      announceRef.current(bookingdata.booking.mainData);
      dispatch(clearBooking());
      setTempRoute(null);
      setLoading2(false);
      navigate('/bookings')
    }
  }, [bookingdata.booking,clearBooking,dispatch,navigate]);

  // A failed booking or estimate is shown in the form; the busy state ends.
  useEffect(() => {
    if (bookingdata.error && bookingdata.error.flag) setLoading2(false);
  }, [bookingdata.error]);

  useEffect(() => {
    if (cartypes && cartypes.length > 0 && carType === t('select_car')) {
      const firstCar = cartypes.sort((a, b) => a.pos - b.pos)[0];
      if (firstCar) {
        setCarType(firstCar.name);
        setInstructionData(prev => ({
          ...prev,
          parcelTypeSelected: Array.isArray(firstCar.parcelTypes) ? firstCar.parcelTypes[0] : null,
          optionSelected: Array.isArray(firstCar.options) ? firstCar.options[0] : null
        }));
        setSelectedCarDetails(firstCar);
      }
    }
  }, [cartypes, carType, t]);

  // Any change to the trip after the fare came back makes that fare stale:
  // go back to step 1 (Get fare).
  const resetFareStep = () => {
    if (estimateModalStatus || estimatedata.estimate) {
      setEstimateModalStatus(false);
      setEstimateRequested(false);
      dispatch(clearEstimate());
      handleRemovePromo();
    }
    setFormError('');
  };

  const handleCarSelect = (event) => {
    resetFareStep();
    setCarType(event.target.value);
    let carDetails = null;
    for (let i = 0; i < cartypes.length; i++) {
      if (cartypes[i].name === event.target.value) {
        carDetails = cartypes[i];
        let instObj = {...instructionData};
        if(Array.isArray(cartypes[i].parcelTypes) && instObj.parcelTypeSelected !== cartypes[i].parcelTypes[0]){
          instObj.parcelTypeSelected = cartypes[i].parcelTypes[0];
          instObj.parcelTypeIndex = 0;
        }
        if(Array.isArray(cartypes[i].options) && instObj.optionSelected !== cartypes[i].options[0]){
          instObj.optionSelected = cartypes[i].options[0];
          instObj.optionIndex = 0;
        }
        setInstructionData(instObj);
      }
    }
    setSelectedCarDetails(carDetails);
  };

  const handleBookTypeSelect = (event) => {
    resetFareStep();
    setBookingType(event.target.value);
    setLaterError('');
    if (event.target.value === 'Book Later' && MinutesPassed(selectedDate) < 15) {
      setSelectedDate(defaultLaterMs());
    }
  };

  const onDateChange = (value) => {
    resetFareStep();
    setLaterError('');
    if (value && typeof value.isValid === 'function' && value.isValid()) {
      setSelectedDate(value.valueOf());
    }
  };

  useEffect(() => {
    if (estimatedata.estimate && estimateRequested) {
      setEstimateRequested(false);
      setEstimateModalStatus(true);
    }
    if (estimatedata.estimate && estimateRequested && settings && !settings.coustomerBidPrice) {
      let  price = estimatedata.estimate.estimateFare;
      let ammount =  settings.coustomerBidPriceType === 'flat' ? parseFloat(price- settings.bidprice).toFixed(settings.decimal) : parseFloat(price - parseFloat(price*(settings.bidprice/100))).toFixed(settings.decimal);
      if(ammount && ammount > 0){setMinimumPrice(ammount)}
    }
    if (userdata.users) {
      let arr = [];
      for (let i = 0; i < userdata.users.length; i++) {
        let user = userdata.users[i];
        if ((user.usertype === 'customer' || user.usertype === 'fleetadmin' || user.usertype === 'callcenteragent' || user.usertype === 'callcentersupervisor') && ((auth.profile.usertype === 'fleetadmin'/* && user.fleetadmin === auth.profile.uid*/) || auth.profile.usertype === 'admin' || auth.profile.usertype === 'callcenteragent' || auth.profile.usertype === 'callcentersupervisor' )) {
          arr.push({
            'firstName': user.firstName,
            'lastName': user.lastName,
            'mobile': user.mobile,
            'email': user.email,
            'uid': user.id,
            'desc': user.firstName + ' ' + user.lastName + ' (' + (settings.AllowCriticalEditsAdmin? user.mobile : t('hidden_demo', { defaultValue: 'Hidden' })) + ') ' + (settings.AllowCriticalEditsAdmin? user.email : t('hidden_demo', { defaultValue: 'Hidden' })),
            'pushToken': user.pushToken ? user.pushToken : ''
          });
        }
      }
      setUsers(arr);
      // Driver list for dispatch is no longer built here — matching moved to the
      // server (dispatchBookingToDrivers), which reads fresh /locations directly.
    }
  }, [estimatedata.estimate, userdata.users, estimateRequested, settings, auth.profile.usertype, auth.profile.uid,t]);


  useEffect(() => {
    if(auth.profile.usertype && (auth.profile.usertype==='customer' || (auth.profile.usertype==='fleetadmin' && auth.profile?.source === 'oromiabank'))){
      setUserCombo({
        'firstName': auth.profile.firstName,
        'lastName': auth.profile.lastName,
        'mobile': auth.profile.mobile,
        'email': auth.profile.email,
        'uid': auth.profile.uid,
        'pushToken': auth.profile.pushToken ? auth.profile.pushToken : ''
      });
    }
  }, [auth.profile]);


  const mobile = !!customerPhone || !!userCombo?.mobile;
  // Staff book for a customer by phone; customers (and the Oromia Bank fleet
  // admin) book for themselves.
  const showPhoneField = !!auth.profile.usertype && auth.profile.usertype !== 'customer' && (auth.profile.usertype !== 'fleetadmin' && auth.profile?.source !== 'oromiabank');

  const phoneProblem = (digits) =>
    digits && !normalizePhone(digits)
      ? t('add_booking_phone_error', { defaultValue: 'Enter the 9 digits of the number, e.g. 911234567.' })
      : '';

  const handleGetOptions = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setFormError('');
    // The phone is checked here, next to its field, before any fare request.
    if (showPhoneField) {
      const problem = customerPhone
        ? phoneProblem(customerPhone)
        : t('add_booking_phone_missing', { defaultValue: "Enter the customer's phone number." });
      if (problem) {
        setPhoneError(problem);
        if (phoneRef.current) phoneRef.current.focus();
        return;
      }
    }
    setLoadingOptions(true);
    try {
    // Approval gate (settings.approvalGateEnabled, common earningGate): a
    // customer whose account is on hold (approved !== true) cannot book. The
    // database rules refuse that booking anyway; this says why, before any
    // estimate or payment. Bookings staff make for a customer are not gated
    // (the rules gate only a customer booking for themselves).
    if (auth.profile.usertype === 'customer' && typeof api.ownAccountBlock === 'function' && api.ownAccountBlock(auth.profile, settings)) {
      const phone = typeof api.officePhone === 'function' ? api.officePhone(settings) : '9260';
      setFormError(t('gate_rider_on_hold', { phone, defaultValue: "Your account is on hold, so you can't book trips right now. Call {{phone}} for help." }));
      return;
    }
    if((settings && settings.imageIdApproval && auth.profile.usertype ==='customer' && auth.profile.verifyId) || auth.profile.usertype==='admin' || auth.profile.usertype==='callcenteragent' || auth.profile.usertype==='callcentersupervisor' || (settings && !settings.imageIdApproval && auth.profile.usertype ==='customer') || auth.profile.usertype === 'fleetadmin'){
      if(!(settings && settings.disable_cash && (auth.profile.usertype==='admin'|| auth.profile.usertype === 'fleetadmin')) || auth.profile.usertype==='customer' || auth.profile.usertype==='callcenteragent' || auth.profile.usertype==='callcentersupervisor' || auth.profile.usertype==='finance'){
        if((auth.profile.usertype==='customer' && parseFloat(auth.profile.walletBalance)>=0) || auth.profile.usertype==='admin' || auth.profile.usertype==='callcenteragent' || auth.profile.usertype==='callcentersupervisor' || auth.profile.usertype === 'fleetadmin'){
          setEstimateRequested(true);
          if (pickupAddress && dropAddress && selectedCarDetails) {
                  const route = await getDirectionsApi(pickupAddress.coords.lat + "," + pickupAddress.coords.lng, dropAddress.coords.lat + ',' + dropAddress.coords.lng, null);
                  setTempRoute(route);
                  if(pickupAddress && dropAddress){
                    if (bookingType === 'Book Now') {
                      if(mobile === true){
                        if(Array.isArray(selectedCarDetails.options) || Array.isArray(selectedCarDetails.parcelTypes)){
                          setOptionModalStatus(true);
                        }else{
                          let estimateRequest = {
                            pickup: pickupAddress,
                            drop: dropAddress,
                            carDetails: selectedCarDetails,
                            instructionData: instructionData,
                            routeDetails: route
                          };
                          dispatch(getEstimate(estimateRequest));
                        }
                      }else{
                        setFormError(t('incomplete_user', { defaultValue: 'The customer profile is incomplete.' }));
                      }
                    } else {
                        if (bookingType === 'Book Later' && selectedDate) {
                          if (MinutesPassed(selectedDate) >= 15) {
                            if(mobile === true){
                              if(Array.isArray(selectedCarDetails.options) || Array.isArray(selectedCarDetails.parcelTypes)){
                                setOptionModalStatus(true);
                              }else{
                                let estimateRequest = {
                                  pickup: pickupAddress,
                                  drop: dropAddress,
                                  carDetails: selectedCarDetails,
                                  instructionData: instructionData,
                                  routeDetails: route
                                };
                                dispatch(getEstimate(estimateRequest));
                              }
                            }else{
                              setFormError(t('incomplete_user', { defaultValue: 'The customer profile is incomplete.' }));
                            }
                          } else {
                            setLaterError(t('add_booking_later_too_soon', { defaultValue: 'Pick a time at least 15 minutes from now.' }));
                          }
                        } else {
                          setFormError(t('add_booking_missing_fields', { defaultValue: 'Choose the pickup, the destination and the car type.' }));
                        }
                      }
                  } else {
                    setFormError(t('place_to_coords_error', { defaultValue: 'That place could not be found on the map. Try again.' }))
                  }
          } else {
            setFormError(t('add_booking_missing_fields', { defaultValue: 'Choose the pickup, the destination and the car type.' }))
          }
        }else{
          setFormError(t('wallet_balance_low', { defaultValue: 'Your wallet balance is too low.' }))
        }
      } else {
        setFormError(t('cash_booking_false', { defaultValue: 'Bookings can\'t be made while cash payment is off.' }))
      }
    } else {
      setFormError(t('verifyid_error', { defaultValue: 'An ID or passport is needed first.' }))
    }
  } finally {
    setLoadingOptions(false);
  }
};

  const handleWalletPayment = (e) => {
    e.preventDefault();
    let curBooking = { ...bookingdata.booking.mainData };
    if(parseFloat(curBooking.trip_cost)>parseFloat(auth.profile.walletBalance) && payCat === 'wallet'){
      showToast.error(t('wallet_balance_low', { defaultValue: 'Your wallet balance is too low.' }));
    }else{
      // Driver matching is SERVER-SIDE now (dispatchBookingToDrivers): writing
      // the booking to status NEW triggers the Cloud Function, which finds fresh
      // nearby drivers from live /locations timestamps. We write an empty
      // requestedDrivers and let the server populate it.
      let requestedDrivers = {};
      curBooking.prepaid = true;
      curBooking.status = 'NEW';
      curBooking.payment_mode = "wallet";
      curBooking.customer_paid = parseFloat(curBooking.trip_cost).toFixed(settings.decimal);
      curBooking.discount = 0;
      curBooking.usedWalletMoney = parseFloat(curBooking.trip_cost).toFixed(settings.decimal);
      curBooking.cardPaymentAmount = 0;
      curBooking.cashPaymentAmount = 0;
      curBooking.payableAmount = 0;
      curBooking.requestedDrivers = requestedDrivers;

      dispatch(updateBooking(curBooking));

      setTimeout(()=>{
        dispatch(clearEstimate());
        setPaymentModalStatus(false);
        setWalletModalStatus(false);
        clearForm();
        dispatch(clearBooking());
        setTempRoute(null);
        announceBooking(curBooking);
        navigate('/bookings')
      },1500);
    }
  }

  const handleGetEstimate = (e) => {
    e.preventDefault();
    setOptionModalStatus(false);
    let estimateRequest = {
      pickup: pickupAddress,
      drop: dropAddress,
      carDetails: selectedCarDetails,
      instructionData: instructionData,
      routeDetails: tempRoute
    };
    dispatch(getEstimate(estimateRequest));
  };

  const confirmBooking = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setFormError('');
    setLoading2(true);
    let requestedDrivers = {};
    let driverEstimates = {};

    let notfound = true;
    if (activeBookings) {
      for (let i = 0; i < activeBookings.length; i++) {
        if (activeBookings[i].payment_mode === 'wallet' && activeBookings[i].status !== 'PAID') {
          notfound = false;
          break;
        }
      }
    }

    if ((payCat === 'wallet' && notfound && (auth.profile.usertype === 'customer' || auth.profile?.source === 'oromiabank')) || payCat !== 'wallet' || auth.profile.usertype === 'admin' || auth.profile.usertype === 'callcenteragent' || auth.profile.usertype === 'callcentersupervisor' || auth.profile.usertype === 'fleetadmin') {
      const regx1 = /([0-9\s-]{7,})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/;
      if ((otherPerson && /\S/.test(instructionData.otherPerson) && regx1.test(instructionData.otherPersonPhone) && instructionData.otherPersonPhone && instructionData.otherPersonPhone.length > 6) || !otherPerson) {
        if ((offerFare > 0 && offerFare >= parseFloat(minimumPrice)) || offerFare === '') {
          if ((payCat === 'wallet' && (parseFloat(auth.profile.walletBalance) >= parseFloat(estimatedata.estimate.estimateFare)) && !calcEst && (auth.profile.usertype === 'customer' || auth.profile?.source === 'oromiabank')) || payCat !== 'wallet' || (payCat === 'wallet' && calcEst && (auth.profile.usertype === 'customer' || auth.profile?.source === 'oromiabank')) || auth.profile.usertype === 'admin' || auth.profile.usertype === 'fleetadmin' || auth.profile.usertype === 'callcenteragent' || auth.profile.usertype === 'callcentersupervisor') {
            // Driver matching + dispatch is SERVER-AUTHORITATIVE now
            // (functions/index.js dispatchBookingToDrivers). The booking is
            // written with status NEW and an empty requestedDrivers; the Cloud
            // Function finds fresh nearby drivers from live /locations timestamps
            // and writes requestedDrivers, fanning out the push via
            // onRequestedDriverChanged. The call-center agent is no longer
            // blocked on driver availability, and we no longer scan the (no
            // freshness check) driver list here.

            // The booking is for the number in the phone field, as before.
            // Without that field (customer, fleet admin) there is no number, so
            // the booking stops here, as it always has; the message now shows
            // in the form instead of an alert over a spinner that never ended.
            const normPhone = normalizePhone(customerPhone);
            if (!normPhone) {
              setLoading2(false);
              if (showPhoneField) {
                setPhoneError(t('add_booking_phone_error', { defaultValue: 'Enter the 9 digits of the number, e.g. 911234567.' }));
                if (phoneRef.current) phoneRef.current.focus();
              } else {
                setFormError(t('phone_error_msg', { defaultValue: 'Enter a valid phone number.' }));
              }
              return;
            }
            let rider;
            try {
              rider = await ensureUser(normPhone);
            } catch (err) {
              setLoading2(false);
              setFormError((err && err.message) || t('add_booking_failed', { defaultValue: 'The booking could not be made. Try again.' }));
              return;
            }
            const promoFields = appliedPromo ? {
              promo_applied: true,
              promo_details: appliedPromo,
              discount: parseFloat(promoDiscount).toFixed(settings.decimal)
            } : {
              promo_applied: false,
              promo_details: null,
              discount: 0
            };
            let bookingObject = {
              pickup: pickupAddress,
              drop: dropAddress,
              carDetails: selectedCarDetails,
              userDetails: {
                uid: rider.uid || userCombo.uid,
                firstName: rider?.firstName || userCombo?.firstName,
                lastName: rider?.lastName || userCombo?.lastName,
                mobile: rider?.mobile || userCombo?.mobile,
                pushToken: rider?.pushToken || userCombo?.pushToken,
                email: rider?.email || userCombo?.email
              },
              estimate: estimatedata.estimate,
              instructionData: instructionData,
              tripInstructions: tripInstructions,
              roundTrip: roundTrip === 0 ? false : true,
              tripdate: bookingType === 'Book Later' ? new Date(selectedDate).getTime() : new Date().getTime(),
              bookLater: bookingType === 'Book Later' ? true : false,
              settings: settings,
              booking_type_admin: auth.profile.usertype === 'admin' ? true : false,
              booking_type_fleetadmin: auth.profile.usertype === 'fleetadmin' ? true : false,
              booking_type_call_center: auth.profile.usertype === 'callcenteragent' || auth.profile.usertype === 'callcentersupervisor',
              fleetadmin: auth.profile.usertype === 'fleetadmin' ? auth.profile.uid : null,
              callcenteragent: auth.profile.usertype === 'callcenteragent' ? auth.profile.uid : null,
              callcentersupervisor: auth.profile.usertype === 'callcentersupervisor' ? auth.profile.uid : null,
              requestedDrivers: calcEst ? requestedDrivers : (optionsRequired && payCat === 'cash') ? requestedDrivers : (settings.prepaid && payCat === 'cash') ? requestedDrivers : !settings.prepaid ? requestedDrivers : requestedDrivers,
              driverEstimates: driverEstimates,
              payment_mode: (auth.profile.usertype === 'admin') ? 'cash' : payCat,
              booking_from_web: true,
              deliveryWithBid: auth.profile.usertype === 'customer' ? deliveryWithBid === 0 ? false : true : false,
              offerFare: (offerFare && offerFare > 0) ? offerFare : 0,
              customer_offer: (offerFare && offerFare > 0) ? offerFare : 0,
              ...promoFields
            };

            if (auth.profile.usertype === 'customer' && !(auth.profile.firstName && auth.profile.lastName && auth.profile.email && auth.profile.firstName.length > 0 && auth.profile.lastName.length > 0 && auth.profile.email.length > 0)) {
              // const regx1 = /([0-9\s-]{7,})(?:\s*(?:#|x\.?|ext\.?|extension)\s*(\d+))?$/;
              // if ((optionsRequired && /\S/.test(instructionData.deliveryPerson) && regx1.test(instructionData.deliveryPersonPhone) && instructionData.deliveryPersonPhone && instructionData.deliveryPersonPhone.length > 6) || !optionsRequired){
              if ((profileData.firstName && profileData.firstName.length > 0 && profileData.lastName && profileData.lastName.length > 0) || (auth.profile.firstName && auth.profile.firstName.length > 0 && auth.profile.lastName && auth.profile.lastName.length > 0)) {
                const re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
                if ((re.test(profileData.email) && !auth.profile.email) || (auth.profile.email && auth.profile.email.length > 0)) {
                  setLoading(true);
                  checkUserExists({email: profileData.email}).then(async (res) => {
                    const decryptedRes = await processApiResponse(res);
                    if (decryptedRes.users && decryptedRes.users.length > 0) {
                      setLoading(false);
                      setLoading2(false);
                      setFormError(t('user_exists', { defaultValue: 'A user with this email already exists.' }));
                    } else if (decryptedRes.error) {
                      setLoading(false);
                      setLoading2(false);
                      setFormError(t('email_or_mobile_issue', { defaultValue: 'Check the email address or phone number.' }));
                    } else {
                      setLoading(false);
                      if (payCat === 'card') {
                        const fareNum = parseFloat(estimatedata.estimate.estimateFare);
                        const discountNum = appliedPromo ? parseFloat(promoDiscount) : 0;
                        const payable = parseFloat(fareNum - discountNum).toFixed(settings.decimal);
                        const paymentPacket = {
                          payment_mode: 'card',
                          customer_paid: payable,
                          cardPaymentAmount: payable,
                          discount: parseFloat(discountNum).toFixed(settings.decimal),
                          usedWalletMoney: 0,
                          cashPaymentAmount: 0,
                          promo_applied: !!appliedPromo,
                          promo_details: appliedPromo || null,
                          payableAmount: payable,
                        };
                        bookingObject['paymentPacket'] = paymentPacket;
                      }

                      const result = validateBookingObj(t, bookingObject, instructionData, otherPerson);
                      if (result.error) {
                        setLoading2(false);
                        setFormError(result.msg);
                      } else {
                        profileData['uid'] = auth.profile.uid;
                        let bookingData = result.bookingObject;
                        bookingData.userDetails.firstName = profileData.firstName;
                        bookingData.userDetails.lastName = profileData.lastName;
                        bookingData.userDetails.email = profileData.email;
                        if (appliedPromo) {
                          const riderUid = bookingData.userDetails.uid;
                          const promoUpdate = { ...appliedPromo };
                          promoUpdate.user_avail = promoUpdate.user_avail ? parseInt(promoUpdate.user_avail) + 1 : 1;
                          promoUpdate.usersUsed = { ...(promoUpdate.usersUsed || {}), [riderUid]: true };
                          dispatch(editPromo(promoUpdate));
                        }
                        setBookingOnWait(bookingData);
                      }
                      setTimeout(() => {
                        dispatch(updateProfileWithEmail(profileData));
                      }, 200)
                    }
                  });
                } else {
                  setLoading2(false);
                  setFormError(t('proper_email', { defaultValue: 'Enter a valid email address.' }))
                }
              } else {
                setLoading2(false);
                setFormError(t('proper_input_name', { defaultValue: 'Enter the first and last name.' }))
              }
              // } else {
              //   setFormError(t('deliveryDetailMissing'))
              // }
            } else {
              if (payCat === 'card') {
                const fareNum = parseFloat(estimatedata.estimate.estimateFare);
                const discountNum = appliedPromo ? parseFloat(promoDiscount) : 0;
                const payable = parseFloat(fareNum - discountNum).toFixed(settings.decimal);
                const paymentPacket = {
                  payment_mode: 'card',
                  customer_paid: payable,
                  cardPaymentAmount: payable,
                  discount: parseFloat(discountNum).toFixed(settings.decimal),
                  usedWalletMoney: 0,
                  cashPaymentAmount: 0,
                  promo_applied: !!appliedPromo,
                  promo_details: appliedPromo || null,
                  payableAmount: payable,
                };
                bookingObject['paymentPacket'] = paymentPacket;
              }
              const result = validateBookingObj(t, bookingObject, instructionData, otherPerson);
              if (result.error) {
                setLoading2(false);
                setFormError(result.msg);
              } else {
                setEstimateModalStatus(false);
                if ((auth.profile.usertype === 'callcenteragent' || auth.profile.usertype === 'callcentersupervisor' || auth.profile.usertype === 'fleetadmin') && startCode && settings.otp_secure) {
                  result.bookingObject['otp'] = startCode
                }
                if (appliedPromo) {
                  const riderUid = result.bookingObject.userDetails.uid;
                  const promoUpdate = { ...appliedPromo };
                  promoUpdate.user_avail = promoUpdate.user_avail ? parseInt(promoUpdate.user_avail) + 1 : 1;
                  promoUpdate.usersUsed = { ...(promoUpdate.usersUsed || {}), [riderUid]: true };
                  dispatch(editPromo(promoUpdate));
                }
                dispatch(addBooking(result.bookingObject));
                handleRemovePromo();
              }
            }
          } else {
            setLoading2(false);
            setFormError(t('wallet_balance_low', { defaultValue: 'Your wallet balance is too low.' }));
          }
        } else {
          setLoading2(false);
          setFormError(t('offer_price_greter_minimum_price', { defaultValue: 'The offer must be at least the minimum price.' }))
        }
      } else {
        setLoading2(false);
        setFormError(t('otherPersonDetailMissing', { defaultValue: 'Enter the other person\'s name and phone number.' }))
      }
    } else {
      setLoading2(false);
      setFormError(t('wallet_booking_alert', { defaultValue: 'There is already an unpaid wallet booking.' }));
    }
  };

  const handleOptionModalClose = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setOptionModalStatus(false);
  };

  const handleEstimateModalClose = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setEstimateModalStatus(false);
    dispatch(clearEstimate());
    setEstimateRequested(false);
    handleRemovePromo();
    setFormError('');
  };

  const handleEstimateErrorClose = () => {
    dispatch(clearEstimate());
    setEstimateRequested(false);
  };

  const handleBookingErrorClose = () => {
    dispatch(clearBooking());
    setEstimateRequested(false);
  };

  const handleWalletModalClose = (e) => {
    setTimeout(()=>{
      setWalletModalStatus(false);
      dispatch(clearBooking());
      dispatch(clearEstimate());
    },1500);
  }

  const handlePaymentModalClose = (e) => {
    setTimeout(()=>{
      setPaymentModalStatus(false);
      dispatch(clearBooking());
      dispatch(clearEstimate());
    },1500);
  }

  const normalizePhone = (input) => {
    const cleaned = String(input || '').trim().replace(/\D/g, '');
    // +251XXXXXXXXX
    if (/^251[1-9]\d{8}$/.test(cleaned))   return `+${cleaned}`;
    // national without leading zero
    if (/^[1-9]\d{8}$/.test(cleaned))      return `+251${cleaned}`;
    // local with leading zero
    if (/^0[1-9]\d{8}$/.test(cleaned))     return `+251${cleaned.slice(1)}`;
    return null;
  };

  const ensureUser = async (normPhone) => {
    // try existing in local users list
    const existing = users?.find(u => u.mobile === normPhone);
    if (existing) {
      setUserCombo(existing);
      return existing;
    } else {
      // local users ref may not have full data scope — check Firebase Auth directly
      const result = await checkUserExists({ mobile: normPhone });
      if (result?.users && result.users.length > 0) {
        const profileRes = await fetchSingleProfile({ mobile: normPhone });
        const uid = Object.keys(profileRes.user)[0];
        const rider = { ...profileRes.user[uid], uid };
        setUserCombo({
          'firstName': rider.firstName,
          'lastName': rider.lastName,
          'mobile': rider.mobile,
          'email': rider.email,
          'uid': rider.uid,
          'pushToken': rider.pushToken ? rider.pushToken : ''
        });
        return rider;
      }
    }
    // otherwise register
    const regData = {
      mobile:   normPhone,
      usertype: 'customer',
      firstName:'Manual',
      lastName: 'Rider',
      email:    `${normPhone.replace(/^\+/, '')}@donkeydrive.io`,
      source:   'booking',
    };
    const newRes = await mainSignUp(regData);
    if (newRes.error) {
      throw new Error(newRes.error);
    }
    // fetch profile to get uid
    const profileRes = await fetchSingleProfile({ mobile: normPhone });
    const uid = Object.keys(profileRes.user)[0];

    const rider = { ...profileRes.user[uid], uid };
    setUserCombo({
      'firstName': rider.firstName,
      'lastName': rider.lastName,
      'mobile': rider.mobile,
      'email': rider.email,
      'uid': rider.uid,
      'pushToken': rider.pushToken ? rider.pushToken : ''
    })
    await sendWelcomeSms(rider.mobile, rider.customerCode);
    return rider;
  };

  // ── What the form shows ──────────────────────────────────────────────
  const isStaffCalc = !!auth.profile && (auth.profile.usertype === 'callcenteragent' || auth.profile.usertype === 'callcentersupervisor' || auth.profile.usertype === 'admin');
  const estimate = estimatedata.estimate;
  const fareReady = estimateModalStatus && !!estimate;
  const busy = loadingOptions || loading2 || loading;
  const hideFareAmount = showEst && settings && settings.hasOwnProperty("bookingFlow") && (settings.bookingFlow === "0" || settings.bookingFlow === "2");
  const unitWord = settings && settings.convert_to_mile ? t('mile', { defaultValue: 'mi' }) : t('km', { defaultValue: 'km' });
  const fareLine = estimate
    ? [
        t('fare', { defaultValue: 'Fare' }),
        Number(estimate.estimateDistance) > 0 ? `${estimate.estimateDistance} ${unitWord}` : null,
        Number(estimate.estimateTime) > 0 ? `${Math.max(1, Math.round(Number(estimate.estimateTime) / 60))} ${t('min', { defaultValue: 'min' })}` : null,
      ].filter(Boolean).join(' · ')
    : '';
  const estimateFareNum = estimate ? parseFloat(estimate.estimateFare) || 0 : 0;
  const payableNum = appliedPromo ? Math.max(0, estimateFareNum - (parseFloat(promoDiscount) || 0)) : estimateFareNum;
  // Owner rule: Estimate unless the customer set the price (a bid offer).
  const fareKind = isFixedFare({ offerFare }) ? 'fixed' : 'estimate';
  const showStartCode = !!(settings && settings.otp_secure && startCode && (auth.profile.usertype === 'callcenteragent' || auth.profile.usertype === 'callcentersupervisor' || auth.profile.usertype === 'fleetadmin'));
  const showPaymentChoice = auth.profile.usertype === 'customer' || auth.profile.usertype === 'fleetadmin' || auth.profile.usertype === 'callcenteragent' || auth.profile.usertype === 'callcentersupervisor';
  const showPromo = (auth.profile.usertype === 'admin' || auth.profile.usertype === 'callcenteragent' || auth.profile.usertype === 'callcentersupervisor' || auth.profile.usertype === 'fleetadmin') && getActivePromos().length > 0;
  const money = (v) => formatMoney(v, settings);
  const sortedCars = Array.isArray(cartypes) ? cartypes.slice().sort((a, b) => a.pos - b.pos) : [];
  const amountOf = (element) => (settings.swipe_symbol === false ? `${settings.symbol} ${element.amount}` : `${element.amount} ${settings.symbol}`);

  const reduxError = (bookingdata.error && bookingdata.error.flag && bookingdata.error.msg)
    ? { msg: bookingdata.error.msg, close: handleBookingErrorClose }
    : (estimatedata.error && estimatedata.error.flag && estimatedata.error.msg)
      ? { msg: estimatedata.error.msg, close: handleEstimateErrorClose }
      : null;

  const submitForm = (e) => {
    e.preventDefault();
    if (busy) return;
    if (fareReady) confirmBooking(e);
    else handleGetOptions(e);
  };

  const renderBookingForm = () => (
    <Paper
      component="form"
      noValidate
      onSubmit={submitForm}
      aria-labelledby="add-booking-title"
      sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2, direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}
    >
      <Box>
        <Typography id="add-booking-title" component="h1" sx={{ fontSize: 20, fontWeight: 700, color: c.INK, lineHeight: 1.4 }}>
          {t('nav_add_booking', { defaultValue: 'Add booking' })}
        </Typography>
        <Typography sx={{ fontSize: 14, color: c.INK_2, mt: 0.5, display: { lg: 'none' } }}>
          {t('add_booking_intro', { defaultValue: 'Get the fare first, then book. When a driver accepts, the customer gets an SMS with the start code.' })}
        </Typography>
      </Box>

      {/* Two halves on wide screens, in reading order: who and where on the
          left, the fare and the booking on the right. Below lg one column,
          as before. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 7fr) minmax(0, 5fr)' }, gap: { xs: 2, lg: 4 }, alignItems: 'start' }}>
        {/* Step 1, the trip: phone, pickup, destination, car type | when,
            trip time. */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' }, gap: 2, alignItems: 'start', minWidth: 0 }}>
          {showPhoneField ? (
            <TextField
              inputRef={phoneRef}
              autoFocus
              label={t('add_booking_customer_phone', { defaultValue: 'Customer phone' })}
              fullWidth
              value={customerPhone}
              disabled={busy}
              onChange={(e) => {
                const digits = toNineDigits(e.target.value);
                if (digits !== customerPhone) resetFareStep();
                setCustomerPhone(digits);
                if (phoneError && !phoneProblem(digits) && digits.length === 9) setPhoneError('');
              }}
              onBlur={() => setPhoneError(phoneProblem(customerPhone))}
              error={!!phoneError}
              helperText={phoneError || t('add_booking_phone_help', { defaultValue: '9 digits, e.g. 911234567' })}
              inputProps={{ inputMode: 'numeric', autoComplete: 'tel-national' }}
              type="tel"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {`+${settings.countryCode || '251'}`}
                  </InputAdornment>
                )
              }}
              sx={{ gridColumn: FULL_ROW }}
            />
          ) : null}

          <Box sx={{ gridColumn: FULL_ROW, minWidth: 0 }}>
            <GoogleMapsAutoComplete
              variant="outlined"
              placeholder={t('pickup', { defaultValue: 'Pickup' })}
              startAdornment={placeMark(PlaceRoundedIcon, tones.success)}
              value={pickupAddress}
              onChange={(value) => {
                resetFareStep();
                setPickupAddress(value);
              }}
            />
          </Box>
          <Box sx={{ gridColumn: FULL_ROW, minWidth: 0 }}>
            <GoogleMapsAutoComplete
              variant="outlined"
              placeholder={t('add_booking_destination', { defaultValue: 'Destination' })}
              startAdornment={placeMark(FlagRoundedIcon, tones.error)}
              value={dropAddress}
              onChange={(value) => {
                resetFareStep();
                setDropAddress(value);
              }}
            />
          </Box>

          {cartypes ? (
            <TextField
              select
              fullWidth
              id="add-booking-car-type"
              label={t('add_booking_car_type', { defaultValue: 'Car type' })}
              value={carType}
              onChange={handleCarSelect}
              disabled={busy}
              SelectProps={{ MenuProps: { disableScrollLock: true } }}
            >
              <MenuItem value={t('select_car')} key={t('select_car')} disabled>
                {t('select_car')}
              </MenuItem>
              {sortedCars.map((car) => (
                <MenuItem key={car.name} value={car.name}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
                    {car.image ? <Box component="img" src={car.image} alt="" sx={{ height: 18, width: 'auto' }} /> : null}
                    {t(getLangKey(car.name))}
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          ) : null}
          <TextField
            select
            fullWidth
            id="add-booking-when"
            label={t('add_booking_when', { defaultValue: 'When' })}
            value={bookingType}
            onChange={handleBookTypeSelect}
            disabled={busy}
          >
            <MenuItem value="Book Now">{t('add_booking_when_now', { defaultValue: 'Now' })}</MenuItem>
            <MenuItem value="Book Later">{t('add_booking_when_later', { defaultValue: 'Later (pick a time)' })}</MenuItem>
          </TextField>

          {bookingType === 'Book Later' ? (
            <Box sx={{ gridColumn: FULL_ROW, minWidth: 0 }}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  label={t('add_booking_trip_time', { defaultValue: 'Trip time' })}
                  value={dayjs(selectedDate).tz(ADDIS_TZ)}
                  onChange={onDateChange}
                  timezone={ADDIS_TZ}
                  ampm
                  format="D MMM YYYY, h:mm a"
                  minDateTime={dayjs(Date.now() + QUARTER_MS).tz(ADDIS_TZ)}
                  minutesStep={15}
                  timeSteps={{ hours: 1, minutes: 15 }}
                  disabled={busy}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      error: !!laterError,
                      helperText: laterError || t('add_booking_addis_time', { defaultValue: 'Addis Ababa time, at least 15 minutes from now' }),
                    },
                  }}
                />
              </LocalizationProvider>
            </Box>
          ) : null}
        </Box>

        {/* Step 2: the fare and what goes with it. Before the fare, wide
            screens show how the form works here (the intro moves from
            under the title); below lg nothing shows until the fare is in. */}
        <Box
          sx={{
            display: { xs: fareReady ? 'flex' : 'none', lg: 'flex' },
            flexDirection: 'column',
            gap: 2,
            minWidth: 0,
            // Responsive values, not a [breakpoints.up('lg')] key: that key
            // would replace the lg part of `display` above instead of merging.
            alignSelf: { lg: 'stretch' },
            borderInlineStart: { lg: `1px solid ${c.OUTLINE}` },
            paddingInlineStart: { lg: '32px' },
          }}
        >
          {fareReady ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box
                role="status"
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 1.75, py: 1.5, bgcolor: c.SURFACE_ALT, borderRadius: '10px' }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, color: c.INK_2 }}>{fareLine}</Typography>
                  {!hideFareAmount ? <FareTag kind={fareKind} sx={{ mt: 0.5 }} /> : null}
                </Box>
                {!hideFareAmount ? (
                  <Box sx={{ textAlign: isRTL === 'rtl' ? 'left' : 'right' }}>
                    {appliedPromo ? (
                      <Typography sx={{ fontSize: 14, color: c.TEXT_3, textDecoration: 'line-through', fontVariantNumeric: 'tabular-nums' }}>
                        {money(estimateFareNum)}
                      </Typography>
                    ) : null}
                    <Typography component="p" variant="numeral" sx={{ fontSize: 22, color: c.INK, whiteSpace: 'nowrap' }}>
                      {`${fareKind === 'estimate' ? '~' : ''}${money(payableNum.toFixed(settings.decimal))}`}
                    </Typography>
                  </Box>
                ) : null}
              </Box>

              {showStartCode ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap', px: 1.75, py: 1.25, border: `1px solid ${c.OUTLINE}`, borderRadius: '10px' }}>
                  <Typography sx={{ fontSize: 14, color: c.INK_2 }}>{t('add_booking_start_code', { defaultValue: 'Start code' })}</Typography>
                  <Typography sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '0.12em', fontVariantNumeric: 'tabular-nums', color: c.INK }}>{startCode}</Typography>
                  <Typography sx={{ fontSize: 13, color: c.TEXT_3, flexBasis: { xs: '100%', sm: 'auto' } }}>
                    {t('add_booking_start_code_note', { defaultValue: 'The driver asks for it before the trip starts.' })}
                  </Typography>
                </Box>
              ) : null}

              {showPromo ? (
                appliedPromo ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: 14 }}>
                      {`${appliedPromo.promo_code} — ${t(getLangKey(appliedPromo.promo_name))} (${settings.swipe_symbol === false ? `-${settings.symbol} ${promoDiscount}` : `-${promoDiscount} ${settings.symbol}`})`}
                    </Typography>
                    <Button variant="outlined" color="neutral" size="small" onClick={handleRemovePromo}>
                      {calmCaps(t('remove_promo', { defaultValue: 'Remove promo' }))}
                    </Button>
                  </Box>
                ) : (
                  <TextField
                    select
                    fullWidth
                    label={calmCaps(t('promo_code', { defaultValue: 'Promo code' }))}
                    value=""
                    onChange={(e) => handleApplyPromo(e.target.value)}
                    SelectProps={{ displayEmpty: true, renderValue: () => calmCaps(t('apply_promo', { defaultValue: 'Apply promo' })) }}
                    InputLabelProps={{ shrink: true }}
                  >
                    {getActivePromos().map((p) => (
                      <MenuItem key={p.id} value={p.id}>
                        {`${p.promo_code} — ${t(getLangKey(p.promo_name))} (${p.promo_discount_type === 'percentage' ? `${p.promo_discount_value}%` : (settings.swipe_symbol === false ? `${settings.symbol} ${p.promo_discount_value}` : `${p.promo_discount_value} ${settings.symbol}`)})`}
                      </MenuItem>
                    ))}
                  </TextField>
                )
              ) : null}

              {showPaymentChoice && radioProps.length > 0 ? (
                <FormControl component="fieldset">
                  <FormLabel component="legend" sx={{ fontSize: 13, fontWeight: 600, color: c.INK_2 }}>
                    {calmCaps(t('payment_mode', { defaultValue: 'Payment mode' }))}
                  </FormLabel>
                  <RadioGroup row name="payment_mode" value={payment_mode} onChange={handleChange}>
                    {radioProps.map((element, index) => (
                      <FormControlLabel key={element.cat} value={index} control={<Radio />} label={element.label} sx={{ minHeight: 44, mr: 3 }} />
                    ))}
                  </RadioGroup>
                </FormControl>
              ) : null}

              <BookingModalBody
                classes={NO_CLASSES}
                instructionData={instructionData}
                handleChange={handleChange}
                tripInstructions={tripInstructions}
                roundTrip={roundTrip}
                auth={auth}
                profileData={profileData}
                settings={settings}
                deliveryWithBid={deliveryWithBid}
                otherPerson={otherPerson}
                setOtherPerson={setOtherPerson}
                minimumPrice={minimumPrice}
                offerFare={offerFare}
                estimate={estimate}
              />
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, border: `1px dashed ${c.OUTLINE_STRONG}`, borderRadius: '12px' }}>
              <Box aria-hidden="true" sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: tones.accent.bg, color: tones.accent.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                <CalculateOutlinedIcon sx={{ fontSize: 19 }} />
              </Box>
              <Typography sx={{ fontSize: 14, color: c.INK_2, pt: 0.75 }}>
                {t('add_booking_intro', { defaultValue: 'Get the fare first, then book. When a driver accepts, the customer gets an SMS with the start code.' })}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {formError ? (
        <InlineMessage kind="error" onClose={() => setFormError('')}>{formError}</InlineMessage>
      ) : null}
      {reduxError ? (
        <InlineMessage kind="error" onClose={reduxError.close}>{reduxError.msg}</InlineMessage>
      ) : null}

      {/* One primary button; Cancel is quiet and on the leading side. On a
          phone the row stays at the bottom of the screen while the form
          scrolls, so the button is always in reach (Enter also submits). */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          pt: 0.5,
          [theme.breakpoints.down('sm')]: {
            position: 'sticky',
            bottom: 0,
            zIndex: 2,
            mx: -2,
            mb: -2,
            px: 2,
            pt: 1,
            pb: 'calc(8px + env(safe-area-inset-bottom))',
            bgcolor: c.SURFACE,
            borderTop: `1px solid ${c.OUTLINE}`,
          },
        }}
      >
        {fareReady ? (
          <Button type="button" variant="text" color="neutral" onClick={handleEstimateModalClose} disabled={loading2 || loading}>
            {t('cancel', { defaultValue: 'Cancel' })}
          </Button>
        ) : null}
        <Button
          type="submit"
          variant="contained"
          size="large"
          disabled={busy}
          startIcon={busy ? <CircularProgress size={18} color="inherit" /> : fareReady ? <CheckRoundedIcon /> : <DirectionsCarFilledRoundedIcon />}
          sx={{ ml: 'auto', minWidth: 160 }}
        >
          {fareReady
            ? (loading2 || loading ? t('add_booking_booking', { defaultValue: 'Booking…' }) : t('add_booking_book_now', { defaultValue: 'Book now' }))
            : (loadingOptions ? t('add_booking_getting_fare', { defaultValue: 'Getting the fare…' }) : t('add_booking_get_fare', { defaultValue: 'Get fare' }))}
        </Button>
      </Box>
    </Paper>
  );

  const calcRow = (label, amount, caption, sx) => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 1.75, py: 1.25, borderRadius: '10px', bgcolor: c.SURFACE_ALT, ...(sx || {}) }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 15 }}>{label}</Typography>
        {caption ? <Typography sx={{ fontSize: 12.5, color: c.TEXT_3 }}>{caption}</Typography> : null}
      </Box>
      <Typography sx={{ fontSize: 16, fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{amount}</Typography>
    </Box>
  );

  const renderFareCalculator = () => (
    <Paper sx={{ p: { xs: 2, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2, direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
      <Box>
        <Typography component="h1" sx={{ fontSize: 20, fontWeight: 700, color: c.INK, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CalculateOutlinedIcon sx={{ color: c.ACCENT }} aria-hidden="true" />
          {calmCaps(t('fare_calculator_tool', { defaultValue: 'Fare calculator' }))}
        </Typography>
        <Typography sx={{ fontSize: 14, color: c.INK_2, mt: 0.5, display: { lg: 'none' } }}>
          {t('fare_calc_intro', { defaultValue: 'Works out a fare from distance and time: base fare + distance × rate per km + time × rate per hour + convenience fee.' })}
        </Typography>
      </Box>

      {/* Two halves on wide screens, like the booking form: the inputs on
          the left, the receipt on the right. Below lg one column, as before. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'minmax(0, 1fr)', lg: 'minmax(0, 7fr) minmax(0, 5fr)' }, gap: { xs: 2, lg: 4 }, alignItems: 'start' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 0 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr', md: 'repeat(3, 1fr) auto', lg: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
            <TextField
              select
              fullWidth
              label={calmCaps(t('select_car_type', { defaultValue: 'Car type' }))}
              value={selectedCalcCarType?.name || ''}
              onChange={(e) => {
                const selected = cartypes.find(car => car.name === e.target.value);
                setSelectedCalcCarType(selected);
                setCalculatedFare(null);
              }}
            >
              {(cartypes || []).map((car) => (
                <MenuItem key={car.name} value={car.name}>
                  {t(getLangKey(car.name))}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth
              type="number"
              label={calmCaps(t('distance_km', { defaultValue: 'Distance (km)' }))}
              value={calcDistance}
              onChange={(e) => {
                setCalcDistance(e.target.value);
                setCalculatedFare(null);
              }}
              inputProps={{ inputMode: 'decimal', min: 0, step: '0.1' }}
              InputProps={{ endAdornment: <InputAdornment position="end">{unitWord}</InputAdornment> }}
              helperText={calmCaps(t('enter_trip_distance', { defaultValue: 'Trip distance' }))}
            />
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                type="number"
                label={calmCaps(t('travel_time', { defaultValue: 'Travel time' }))}
                value={calcTime}
                onChange={(e) => {
                  setCalcTime(e.target.value);
                  setCalculatedFare(null);
                }}
                inputProps={{ inputMode: 'decimal', min: 0 }}
                helperText={calmCaps(t('enter_trip_time', { defaultValue: 'Trip duration' }))}
              />
              <TextField
                select
                value={calcTimeUnit}
                onChange={(e) => {
                  setCalcTimeUnit(e.target.value);
                  setCalculatedFare(null);
                }}
                inputProps={{ 'aria-label': t('fare_calc_time_unit', { defaultValue: 'Time unit' }) }}
                sx={{ minWidth: 96 }}
              >
                <MenuItem value="minutes">{t('min', { defaultValue: 'min' })}</MenuItem>
                <MenuItem value="seconds">{t('sec', { defaultValue: 'sec' })}</MenuItem>
              </TextField>
            </Box>
            <Button variant="contained" size="large" onClick={calculateManualFare} startIcon={<CalculateOutlinedIcon />} sx={{ minHeight: 56 }}>
              {t('calculate', { defaultValue: 'Calculate' })}
            </Button>
          </Box>
          {calcError ? <InlineMessage kind="error" onClose={() => setCalcError('')}>{calcError}</InlineMessage> : null}
        </Box>

        {/* The receipt, top to bottom with the total last. Before a
            calculation, wide screens show what the tool does here. */}
        <Box
          sx={{
            display: { xs: calculatedFare ? 'flex' : 'none', lg: 'flex' },
            flexDirection: 'column',
            minWidth: 0,
            alignSelf: { lg: 'stretch' },
            borderInlineStart: { lg: `1px solid ${c.OUTLINE}` },
            paddingInlineStart: { lg: '32px' },
          }}
        >
          {calculatedFare ? (
            <Box component="section" aria-label={calmCaps(t('fare_breakdown', { defaultValue: 'Fare breakdown' }))} sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Typography component="h2" sx={{ fontSize: 16, fontWeight: 700 }}>
                {calmCaps(t('fare_breakdown', { defaultValue: 'Fare breakdown' }))}
              </Typography>
              {calcRow(calmCaps(t('base_fare', { defaultValue: 'Base fare' })), `${settings?.symbol || ''}${calculatedFare.baseFare}`)}
              {parseFloat(calculatedFare.distanceCost) > 0
                ? calcRow(
                    calmCaps(t('distance_charge', { defaultValue: 'Distance charge' })),
                    `${settings?.symbol || ''}${calculatedFare.distanceCost}`,
                    `${calcDistance} ${unitWord} × ${settings?.symbol || ''}${selectedCalcCarType?.rate_per_unit_distance || 0}`
                  )
                : null}
              {parseFloat(calculatedFare.timeCost) > 0
                ? calcRow(
                    calmCaps(t('time_charge', { defaultValue: 'Time charge' })),
                    `${settings?.symbol || ''}${calculatedFare.timeCost}`,
                    `${calcTime} ${calcTimeUnit === 'minutes' ? t('min', { defaultValue: 'min' }) : t('sec', { defaultValue: 'sec' })} × ${settings?.symbol || ''}${selectedCalcCarType?.rate_per_hour || 0}/${t('hour', { defaultValue: 'hour' })}`
                  )
                : null}
              {calculatedFare.minFareApplied ? (
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 1.75, py: 1.25, borderRadius: '10px', bgcolor: c.WARNING_TINT, color: c.WARNING_TEXT }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <WarningAmberRoundedIcon sx={{ fontSize: 20 }} aria-hidden="true" />
                    <Typography sx={{ fontSize: 14, fontWeight: 600, color: 'inherit' }}>
                      {calmCaps(t('minimum_fare_applied', { defaultValue: 'Minimum fare applied' }))}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 16, fontWeight: 700, color: 'inherit', fontVariantNumeric: 'tabular-nums' }}>
                    {`${settings?.symbol || ''}${calculatedFare.tripFare}`}
                  </Typography>
                </Box>
              ) : null}
              {parseFloat(calculatedFare.convenienceFee) > 0
                ? calcRow(calmCaps(t('convenience_fee', { defaultValue: 'Convenience fee' })), `${settings?.symbol || ''}${calculatedFare.convenienceFee}`)
                : null}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, px: 1.75, py: 1.5, borderRadius: '10px', bgcolor: c.ACCENT_TINT, border: `1px solid ${c.OUTLINE}` }}>
                <Typography sx={{ fontSize: 17, fontWeight: 700, color: c.INK }}>{calmCaps(t('total_fare', { defaultValue: 'Total fare' }))}</Typography>
                <Typography variant="numeral" component="p" sx={{ color: c.INK, whiteSpace: 'nowrap' }}>
                  {`${settings?.symbol || ''}${calculatedFare.totalFare}`}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button variant="text" color="neutral" onClick={resetFareCalculator}>
                  {t('reset', { defaultValue: 'Reset' })}
                </Button>
              </Box>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5, p: 2, border: `1px dashed ${c.OUTLINE_STRONG}`, borderRadius: '12px' }}>
              <Box aria-hidden="true" sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: tones.accent.bg, color: tones.accent.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 'none' }}>
                <CalculateOutlinedIcon sx={{ fontSize: 19 }} />
              </Box>
              <Typography sx={{ fontSize: 14, color: c.INK_2, pt: 0.75 }}>
                {t('fare_calc_intro', { defaultValue: 'Works out a fare from distance and time: base fare + distance × rate per km + time × rate per hour + convenience fee.' })}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Paper>
  );

  return (
    <Box ref={rootRef} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Tab Navigation */}
      {isStaffCalc ? (
        <Paper sx={{ px: 1 }}>
          <Tabs
            value={activeTab}
            onChange={(e, newValue) => setActiveTab(newValue)}
            variant="fullWidth"
            aria-label={t('nav_add_booking', { defaultValue: 'Add booking' })}
          >
            <Tab label={calmCaps(t('booking_form', { defaultValue: 'Booking form' }))} icon={<EditNoteOutlinedIcon />} iconPosition="start" />
            <Tab label={calmCaps(t('fare_calculator', { defaultValue: 'Fare calculator' }))} icon={<CalculateOutlinedIcon />} iconPosition="start" />
          </Tabs>
        </Paper>
      ) : null}

      {/* Tab Content - Booking Form */}
      {(activeTab === 0 || !isStaffCalc) ? renderBookingForm() : null}

      {/* Tab Content - Fare Calculator */}
      {activeTab === 1 && isStaffCalc ? renderFareCalculator() : null}

      {/* Parcel types and options for car types that have them */}
      <Dialog open={optionModalStatus} onClose={handleOptionModalClose} fullScreen={phoneScreen} maxWidth="xs" fullWidth aria-labelledby="add-booking-options-title">
        <DialogTitle id="add-booking-options-title">{calmCaps(t('options', { defaultValue: 'Options' }))}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {selectedCarDetails && selectedCarDetails.parcelTypes ? (
            <FormControl component="fieldset">
              <FormLabel component="legend">{calmCaps(t('parcel_types', { defaultValue: 'Parcel types' }))}</FormLabel>
              <RadioGroup name="parcelTypeIndex" value={instructionData.parcelTypeIndex} onChange={handleChange}>
                {selectedCarDetails.parcelTypes.map((element, index) => (
                  <FormControlLabel key={element.description} value={index} control={<Radio />} label={`${amountOf(element)} - ${t(getLangKey(element.description))}`} />
                ))}
              </RadioGroup>
            </FormControl>
          ) : null}
          {selectedCarDetails && selectedCarDetails.options ? (
            <FormControl component="fieldset">
              <FormLabel component="legend">{calmCaps(t('options', { defaultValue: 'Options' }))}</FormLabel>
              <RadioGroup name="optionIndex" value={instructionData.optionIndex} onChange={handleChange}>
                {selectedCarDetails.options.map((element, index) => (
                  <FormControlLabel key={element.description} value={index} control={<Radio />} label={`${amountOf(element)} - ${t(getLangKey(element.description))}`} />
                ))}
              </RadioGroup>
            </FormControl>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="neutral" onClick={handleOptionModalClose}>
            {t('cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button variant="contained" onClick={handleGetEstimate}>
            {t('add_booking_get_fare', { defaultValue: 'Get fare' })}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Customer: pay a new booking from the wallet */}
      <Dialog open={walletModalStatus} onClose={handleWalletModalClose} fullScreen={phoneScreen} maxWidth="xs" fullWidth aria-labelledby="add-booking-wallet-title">
        <DialogTitle id="add-booking-wallet-title">{t('payment', { defaultValue: 'Payment' })}</DialogTitle>
        <DialogContent>
          {bookingdata && bookingdata.booking ? (
            <Typography component="p" variant="numeral" sx={{ fontSize: 30, color: c.INK }}>
              {(settings.swipe_symbol===false? settings.symbol + bookingdata.booking.mainData.trip_cost :  bookingdata.booking.mainData.trip_cost + settings.symbol)}
            </Typography>
          ) : null}
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('use_wallet_balance') + " " + (settings.swipe_symbol===false? settings.symbol + auth.profile.walletBalance :  auth.profile.walletBalance + settings.symbol) + ")"}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="text" color="neutral" onClick={handleWalletModalClose}>
            {t('cancel', { defaultValue: 'Cancel' })}
          </Button>
          <Button variant="contained" onClick={handleWalletPayment}>
            {calmCaps(t('paynow_button', { defaultValue: 'Pay now' }))}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Customer: pay a new booking by card through the chosen provider */}
      <Dialog open={paymentModalStatus} onClose={handlePaymentModalClose} fullScreen={phoneScreen} maxWidth="xs" fullWidth aria-labelledby="add-booking-card-title">
        {providers && selectedProvider && bookingdata && bookingdata.booking ? (
          <form action={selectedProvider.link} method="POST">
            <input type='hidden' name='order_id' value={bookingdata.booking.booking_id}/>
            <input type='hidden' name='amount' value={bookingdata.booking.mainData.trip_cost}/>
            <input type='hidden' name='currency' value={settings.code}/>
            <input type='hidden' name='product_name' value={t('bookingPayment', { defaultValue: 'Booking payment' })}/>
            <input type='hidden' name='first_name' value={auth.profile.firstName}/>
            <input type='hidden' name='last_name' value={auth.profile.lastName}/>
            <input type='hidden' name='quantity' value={1}/>
            <input type='hidden' name='cust_id' value={bookingdata.booking.mainData.customer}/>
            <input type='hidden' name='mobile_no' value={bookingdata.booking.mainData.customer_contact}/>
            <input type='hidden' name='email' value={bookingdata.booking.mainData.customer_email}/>
            <DialogTitle id="add-booking-card-title">{t('payment', { defaultValue: 'Payment' })}</DialogTitle>
            <DialogContent>
              <TextField
                select
                fullWidth
                id="selectedProviderIndex"
                name="selectedProviderIndex"
                value={selectedProviderIndex}
                label={t('bookings_payment_provider', { defaultValue: 'Pay with' })}
                onChange={handleChange}
                sx={{ mt: 1 }}
              >
                {providers.map((provider,index) =>
                  <MenuItem key={provider.name} value={index}><img style={{height:24,margin:7}} src={icons[provider.name]} alt={provider.name}/> </MenuItem>
                )}
              </TextField>
            </DialogContent>
            <DialogActions>
              <Button variant="text" color="neutral" onClick={handlePaymentModalClose}>
                {t('cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button variant="contained" type="submit" onClick={handlePaymentModalClose}>
                {calmCaps(t('paynow_button', { defaultValue: 'Pay now' }))}
              </Button>
            </DialogActions>
          </form>
        ) : null}
      </Dialog>
    </Box>
  );
}
